/* ==========================================================================
   TechNest — variant-picker.js
   Keeps the selected variant, its price, availability, media and the URL in
   sync. Price markup is re-fetched from the section so Liquid formats the
   money — the only things this file writes directly are availability states
   and the hidden variant id.
   ========================================================================== */
(function () {
  'use strict';

  const TN = window.TechNest || {};
  const utils = TN.utils;
  if (!utils) return;

  class TnVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.variants = this.readVariants();
      this.idInput = this.querySelector('[data-tn-variant-id]');

      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-tn-option-input], [data-tn-option-select]')) this.onSelect();
      });

      // Reflect the variant already in the URL, if any.
      this.updateSelectedLabels();
    }

    readVariants() {
      const node = this.querySelector('[data-tn-variant-data]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return [];
      }
    }

    /** Currently chosen value per option position. */
    selectedOptions() {
      const values = [];
      this.querySelectorAll('[data-tn-option-input]:checked').forEach((input) => {
        values[parseInt(input.dataset.optionIndex, 10)] = input.value;
      });
      this.querySelectorAll('[data-tn-option-select]').forEach((select) => {
        values[parseInt(select.dataset.optionIndex, 10)] = select.value;
      });
      return values;
    }

    matchVariant(options) {
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );
    }

    onSelect() {
      const options = this.selectedOptions();
      const variant = this.matchVariant(options);

      this.updateSelectedLabels();
      this.updateAvailability(options);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;
      this.updateUrl(variant);
      this.updateMedia(variant);
      this.refreshSection(variant);

      this.dispatchEvent(
        new CustomEvent('tn:variant:change', { bubbles: true, detail: { variant: variant } })
      );
    }

    updateSelectedLabels() {
      const options = this.selectedOptions();
      this.querySelectorAll('[data-tn-selected-value]').forEach((el) => {
        const index = parseInt(el.dataset.tnSelectedValue, 10);
        if (options[index]) el.textContent = options[index];
      });
    }

    /**
     * Mark option values that cannot be reached from the current selection.
     * They stay selectable — blocking them traps shoppers in a dead end — but
     * are visibly and programmatically flagged as unavailable.
     */
    updateAvailability(options) {
      const groups = this.querySelectorAll('.tn-variants__group');

      groups.forEach((group, groupIndex) => {
        group.querySelectorAll('[data-tn-option-input]').forEach((input) => {
          const trial = options.slice();
          trial[groupIndex] = input.value;

          const exists = this.variants.some((variant) =>
            variant.options.every((value, i) => (i === groupIndex ? value === input.value : value === trial[i]))
          );
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every((value, i) => (i === groupIndex ? value === input.value : value === trial[i]))
          );

          const label = this.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          label.classList.toggle('is-unavailable', !available);
          label.classList.toggle('is-nonexistent', !exists);
          input.setAttribute('aria-describedby', available ? '' : 'VariantUnavailable-' + this.sectionId);
        });

        group.querySelectorAll('[data-tn-option-select] option').forEach((option) => {
          const trial = options.slice();
          trial[groupIndex] = option.value;
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every((value, i) => (i === groupIndex ? value === option.value : value === trial[i]))
          );
          option.textContent = available
            ? option.value
            : utils.format(TN.strings.variantUnavailableOption || '[option]', { option: option.value });
        });
      });
    }

    setUnavailable() {
      const button = document.querySelector('[data-tn-add-button]');
      const label = document.querySelector('[data-tn-add-label]');
      if (button) button.setAttribute('disabled', 'disabled');
      if (label) label.textContent = TN.strings.unavailable;
      utils.announce(TN.strings.unavailableVariant);
    }

    /** Keep the URL shareable and the back button meaningful. */
    updateUrl(variant) {
      if (this.dataset.updateUrl !== 'true' || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.querySelector('tn-product-gallery[data-section-id="' + this.sectionId + '"]');
      if (gallery && typeof gallery.showMedia === 'function') {
        gallery.showMedia(variant.featured_media.id);
      }
    }

    /**
     * Re-render the price, stock state and SKU from Liquid. This is what keeps
     * currency formatting correct in every market.
     */
    async refreshSection(variant) {
      const container = document.querySelector('[data-tn-product-info="' + this.sectionId + '"]');
      if (!container) return;

      container.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error('Variant section fetch failed');
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');

        ['price', 'stock', 'sku', 'delivery', 'installments'].forEach((key) => {
          const source = parsed.querySelector('[data-tn-variant-part="' + key + '"]');
          const target = container.querySelector('[data-tn-variant-part="' + key + '"]');
          if (source && target) target.innerHTML = source.innerHTML;
        });

        // Button state comes from the freshly rendered markup, not from JS logic.
        const sourceButton = parsed.querySelector('[data-tn-add-button]');
        const targetButton = container.querySelector('[data-tn-add-button]');
        if (sourceButton && targetButton) {
          targetButton.disabled = sourceButton.hasAttribute('disabled');
          const sourceLabel = sourceButton.querySelector('[data-tn-add-label]');
          const targetLabel = targetButton.querySelector('[data-tn-add-label]');
          if (sourceLabel && targetLabel) targetLabel.textContent = sourceLabel.textContent.trim();
        }

        const sourceSticky = parsed.querySelector('[data-tn-sticky-price]');
        const targetSticky = document.querySelector('[data-tn-sticky-price]');
        if (sourceSticky && targetSticky) targetSticky.innerHTML = sourceSticky.innerHTML;
      } catch (error) {
        // Leave the previous, still-valid markup in place.
      } finally {
        container.setAttribute('aria-busy', 'false');
      }
    }
  }

  utils.define('tn-variant-picker', TnVariantPicker);
})();
